import { useState } from "react"
import { Flex, Menu, Portal, Text } from "@chakra-ui/react"
import {
    LuBookmark,
    LuBookOpen,
    LuCalendarCheck,
    LuCalendarPlus,
    LuChevronDown,
    LuExternalLink,
    LuFileText,
    LuMapPin,
    LuPresentation,
    LuSparkles,
} from "react-icons/lu"
import {
    BORDER_COLOR,
    CARD_ACCENT_PURPLE,
    CARD_ACCENT_RED,
    LAUNCH_GRADIENT,
    LAUNCH_GRADIENT_HOVER,
    META_COLOR,
    NAVY,
    TITLE_COLOR,
} from "./constants"
import AbstractModal from "components/agenda/AbstractModal"

// Cornflower blue for the location line and its pin.
const LOCATION_COLOR = "#5A86D6"

// Menu items (icons) share the location blue so the menu reads as part of the card.
const menuItemProps = {
    gap: "10px",
    px: "10px",
    py: "10px",
    fontSize: "13px",
    color: TITLE_COLOR,
    cursor: "pointer",
    textDecoration: "none",
    _hover: { bg: "#EEF4FF" },
    _highlighted: { bg: "#EEF4FF" },
    css: { "& svg": { color: LOCATION_COLOR, flexShrink: 0 } },
}

// The gradient pill shared by the Actions menu trigger and a lone action button.
const gradientPillProps = {
    align: "center",
    gap: "5px",
    px: "12px",
    py: "7px",
    borderRadius: "full",
    fontSize: "12px",
    fontWeight: "500",
    lineHeight: 1,
    cursor: "pointer",
    border: "none",
    background: LAUNCH_GRADIENT,
    color: "#fff",
    flexShrink: 0,
    textDecoration: "none",
    transition: "background 0.15s ease",
    _hover: { background: LAUNCH_GRADIENT_HOVER, color: "#fff" },
}

// Link actions marked `newWindow` open in a new tab, leaving the chat where it was.
const newWindowProps = item => (item.newWindow ? { target: "_blank", rel: "noopener noreferrer" } : {})

// When a card has only one action, it gets a plain gradient button instead of the menu.
function SingleActionButton({ item }) {
    const linkProps = item.href
        ? { as: "a", href: item.href, ...newWindowProps(item) }
        : { as: "button", type: "button", onClick: item.onSelect }
    return (
        <Flex
            className="sessionCard__action"
            {...gradientPillProps}
            {...linkProps}
            css={{ "& svg": { width: "13px", height: "13px" } }}
        >
            {item.icon}
            {item.label}
        </Flex>
    )
}

function ActionsMenuItem({ item }) {
    if (item.href) {
        return (
            <Menu.Item value={item.key} asChild {...menuItemProps}>
                <a href={item.href} {...newWindowProps(item)}>
                    {item.icon}
                    {item.label}
                </a>
            </Menu.Item>
        )
    }
    return (
        <Menu.Item value={item.key} onSelect={item.onSelect} {...menuItemProps}>
            {item.icon}
            {item.label}
        </Menu.Item>
    )
}

// All card actions collapse into one gradient "Actions" pill with a dropdown, so the
// actions take a single line on narrow phone cards. Slide actions sit in their own group.
function ActionsMenu({ items, slideItems }) {
    return (
        <Menu.Root positioning={{ placement: "bottom-end" }}>
            <Menu.Trigger asChild>
                <Flex
                    as="button"
                    type="button"
                    className="sessionCard__action sessionCard__actions"
                    {...gradientPillProps}
                    css={{ "&[data-state=open] .sessionCard__chevron": { transform: "rotate(180deg)" } }}
                >
                    Actions
                    <Flex className="sessionCard__chevron" transition="transform 0.15s ease">
                        <LuChevronDown size={13} />
                    </Flex>
                </Flex>
            </Menu.Trigger>
            <Portal>
                <Menu.Positioner>
                    <Menu.Content minW="200px" p="4px" borderRadius="10px" boxShadow="0 4px 16px rgba(0,0,0,0.12)">
                        {items.map(item => (
                            <ActionsMenuItem key={item.key} item={item} />
                        ))}
                        {slideItems.length > 0 && (
                            <>
                                {items.length > 0 && <Menu.Separator mx="6px" />}
                                <Menu.ItemGroup>
                                    <Menu.ItemGroupLabel
                                        px="10px"
                                        pt="6px"
                                        pb="2px"
                                        fontSize="11px"
                                        fontWeight="500"
                                        color={META_COLOR}
                                    >
                                        Slides
                                    </Menu.ItemGroupLabel>
                                    {slideItems.map(item => (
                                        <ActionsMenuItem key={item.key} item={item} />
                                    ))}
                                </Menu.ItemGroup>
                            </>
                        )}
                    </Menu.Content>
                </Menu.Positioner>
            </Portal>
        </Menu.Root>
    )
}

// "8:40 AM" with the AM/PM in small caps.
function PresentationTime({ time }) {
    const match = time?.match(/^(.*?)\s*(AM|PM)$/i)
    if (!match) return time || null
    return (
        <>
            {match[1]}{" "}
            <Text as="span" fontVariantCaps="all-small-caps" mt={0}>
                {match[2]}
            </Text>
        </>
    )
}

// A session's presentations behind a "Show presentations" toggle: start time and title,
// the title underlined and linked to the presentation's event page when a link is available.
function PresentationsList({ presentations }) {
    const [open, setOpen] = useState(false)
    return (
        <Flex direction="column" mt="10px" pt="8px" borderTop="1px solid" borderColor={BORDER_COLOR}>
            <Flex
                as="button"
                type="button"
                align="center"
                gap="4px"
                w="fit-content"
                bg="transparent"
                border="none"
                p={0}
                cursor="pointer"
                fontSize="12px"
                fontWeight="500"
                color={NAVY}
                aria-expanded={open}
                onClick={() => setOpen(o => !o)}
            >
                {open ? "Hide presentations" : "Show presentations"}
                <Flex transition="transform 0.15s ease" transform={open ? "rotate(180deg)" : "none"}>
                    <LuChevronDown size={13} />
                </Flex>
            </Flex>
            {open && (
                <Flex direction="column" gap="6px" mt="8px">
                    {presentations.map(p => (
                        <Flex key={p.id} gap="2px" align="baseline" fontSize="12px" lineHeight={1.35}>
                            <Text
                                as="span"
                                flexShrink={0}
                                minW="50px"
                                fontSize="11px"
                                color={META_COLOR}
                                whiteSpace="nowrap"
                                mt={0}
                            >
                                <PresentationTime time={p.time} />
                            </Text>
                            <Text as="span" color={TITLE_COLOR} mt={0}>
                                {p.url ? (
                                    <Text
                                        as="a"
                                        className="sessionCard__presentationLink"
                                        href={p.url}
                                        color={TITLE_COLOR}
                                        textDecoration="underline"
                                        mt={0}
                                    >
                                        {p.title}
                                    </Text>
                                ) : (
                                    p.title
                                )}
                            </Text>
                        </Flex>
                    ))}
                </Flex>
            )}
        </Flex>
    )
}

/*
    session is the slim card payload ({ id, title, meta, time, location, meta2, ... })
    built from the agent's tool output — not the full Emma event. Anything derived
    from the Emma event (calendar times, slide links) is resolved upstream and passed
    in as props.
 */
export default function InlineSessionCard({
    session,
    onToggleSave,
    onToggleCal,
    isAuthenticated,
    kindLabel,
    appLinkUrl,
    // Calendar payload (title/start/end/location plus display info) built from the
    // agenda; when present with onAddToCalendar, the card shows an "Add to Calendar"
    // button that opens the in-chat calendar drawer.
    calEvent,
    onAddToCalendar,
    // URL of the session's slide PDF (from the Emma event's links); when present with
    // onViewSlides, the card shows a "View Slides" button that opens the in-chat PDF
    // viewer. Only the mobile app webview provides onViewSlides.
    slidesUrl,
    onViewSlides,
    // When present alongside a viewable deck, the card also shows an "Ask about these
    // slides" button that sends a question about the deck's content into the chat.
    onAskAboutSlides,
    // URL of the presentation's abstract (from the Emma event's `abstract` link); when
    // present, the actions include "View abstract", which opens it in a modal.
    abstractUrl,
    // A session's presentations (all descendants, flattened) as { id, title, time, url };
    // when non-empty, the card ends with a "Show presentations" toggle listing them.
    presentations,
    // Path of the session in the website's program guide; when present, the actions include
    // "View in program guide", opening it in a new window. Not provided in the mobile app
    // webview, whose cards link into the app instead (appLinkUrl).
    programGuideUrl,
}) {
    const showCalendar = !!(calEvent && onAddToCalendar)
    const showSlides = !!(slidesUrl && onViewSlides)
    const showAskSlides = showSlides && !!onAskAboutSlides
    // Legacy local "added to calendar" toggle used by the non-tool card path.
    const showLegacyCal = !showCalendar && !!onToggleCal
    // Saving is a logged-in feature gated globally by the CMS `showUserFeatures`
    // flag, which the modal enforces upstream by only providing onToggleSave when
    // the flag is on. Calendar (showCalendar above) is intentionally not gated.
    const showSave = !!(isAuthenticated && onToggleSave)
    const [abstractOpen, setAbstractOpen] = useState(false)

    const actions = []
    const slideActions = []
    if (showCalendar) {
        actions.push({
            key: "cal",
            onSelect: () => onAddToCalendar(calEvent),
            icon: <LuCalendarPlus size={16} />,
            label: "Add to calendar",
        })
    }
    if (showLegacyCal) {
        actions.push({
            key: "legacy-cal",
            onSelect: () => onToggleCal(session.id),
            icon: session.calAdded ? <LuCalendarCheck size={16} /> : <LuCalendarPlus size={16} />,
            label: session.calAdded ? "Added to calendar" : "Add to calendar",
        })
    }
    if (appLinkUrl) {
        // App webview mode: deep link into the native app's event screen.
        actions.push({
            key: "event",
            href: appLinkUrl,
            icon: <LuExternalLink size={16} />,
            label: "View event",
        })
    }
    if (programGuideUrl) {
        actions.push({
            key: "program-guide",
            href: programGuideUrl,
            newWindow: true,
            icon: <LuBookOpen size={16} />,
            label: "View in program guide",
        })
    }
    if (showSave) {
        actions.push({
            key: "save",
            onSelect: () => onToggleSave(session.id),
            icon: <LuBookmark size={16} />,
            label: session.saved ? "Saved" : "Save session",
        })
    }
    if (abstractUrl) {
        actions.push({
            key: "abstract",
            onSelect: () => setAbstractOpen(true),
            icon: <LuFileText size={16} />,
            label: "View abstract",
        })
    }
    if (showSlides) {
        // View opens the in-chat PDF viewer; Summary asks the agent about the deck's content.
        slideActions.push({
            key: "slides-view",
            onSelect: () => onViewSlides({ url: slidesUrl, title: session.title, meta: session.meta, kindLabel }),
            icon: <LuPresentation size={16} />,
            label: "View slides",
        })
        if (showAskSlides) {
            slideActions.push({
                key: "slides-summary",
                onSelect: () =>
                    onAskAboutSlides({ id: session.id, title: session.title, meta: session.meta, kindLabel }),
                icon: <LuSparkles size={16} />,
                label: "Slide summary",
            })
        }
    }
    const actionCount = actions.length + slideActions.length

    // Split time/location when the payload provides them; otherwise fall back to
    // the combined meta line.
    const hasSplitMeta = !!(session.time || session.location)
    // Top-level sessions often carry their own title as the breadcrumb; skip the repeat.
    const subtitle =
        session.meta2 && session.meta2.toLowerCase() !== session.title?.toLowerCase() ? session.meta2 : null

    // Presentations get a slides icon before the title and a dark left edge, so they read
    // differently from sessions in a mixed list.
    const isPresentation = kindLabel === "Presentation"
    // Lines under a presentation title line up with the title text, past the icon (16px + 7px gap).
    const indent = isPresentation ? "23px" : 0

    return (
        // Card text renders as divs, not <p>: the bot bubble's markdown styles
        // (`p { margin: 0 }`, `p + p { margin-top }`) would otherwise override the spacing.
        <Flex
            bg="#fff"
            boxShadow={`${isPresentation ? `inset 4px 0 0 ${NAVY}, ` : ""}2px 2px 6px rgba(0, 0, 0, 0.15)`}
            borderRadius="14px"
            overflow="hidden"
            my="10px"
        >
            <Flex direction="column" flex={1} minW={0} px="14px" py="12px">
                {(kindLabel || session.time) && (
                    <Flex justify="space-between" align="baseline" gap="8px">
                        <Text
                            as="div"
                            fontSize="10px"
                            fontWeight="500"
                            letterSpacing="0.05em"
                            textTransform="uppercase"
                            color={CARD_ACCENT_RED}
                            mt={0}
                        >
                            {kindLabel}
                        </Text>
                        {session.time && (
                            <Text
                                as="div"
                                fontSize="12px"
                                fontWeight="500"
                                color={CARD_ACCENT_PURPLE}
                                whiteSpace="nowrap"
                                mt={0}
                            >
                                {session.time}
                            </Text>
                        )}
                    </Flex>
                )}
                <Flex gap="7px" align="flex-start" mt="5px" mb="4px">
                    {isPresentation && (
                        <Flex flexShrink={0} color={NAVY} mt="2px">
                            <LuPresentation size={16} />
                        </Flex>
                    )}
                    <Text as="div" fontSize="15px" fontWeight="400" color={TITLE_COLOR} lineHeight={1.3} noOfLines={3}>
                        {session.title}
                    </Text>
                </Flex>
                {subtitle && (
                    <Text as="div" fontSize="12px" color={META_COLOR} lineHeight={1.4} mt={0} pl={indent}>
                        {subtitle}
                    </Text>
                )}
                {/* Location and the Actions pill share a line to keep the card short. The location
                    keeps its one-line width (flex-basis auto, no shrink) up to the card width, so
                    when it would have to wrap beside the pill, the pill drops to its own line instead. */}
                <Flex align="center" justify="space-between" flexWrap="wrap" columnGap="8px" rowGap="6px" mt="6px">
                    <Flex direction="column" flex="1 0 auto" maxW="100%">
                        {hasSplitMeta ? (
                            session.location && (
                                <Flex
                                    align="flex-start"
                                    gap="4px"
                                    fontSize="12px"
                                    lineHeight={1.4}
                                    color={LOCATION_COLOR}
                                    mt={0}
                                    pl={indent}
                                >
                                    {/* One line tall, so the pin centers on the first line when the label wraps. */}
                                    <Flex flexShrink={0} h="1.4em" align="center">
                                        <LuMapPin size={13} />
                                    </Flex>
                                    <Text as="span" mt={0}>
                                        {session.location}
                                    </Text>
                                </Flex>
                            )
                        ) : (
                            <Text as="div" fontSize="12px" color={LOCATION_COLOR} mt={0} pl={indent}>
                                {session.meta}
                            </Text>
                        )}
                    </Flex>
                    {actionCount > 0 && (
                        // ml="auto" keeps the pill right-aligned when it wraps onto its own line.
                        <Flex ml="auto">
                            {actionCount === 1 && <SingleActionButton item={actions[0] || slideActions[0]} />}
                            {actionCount > 1 && <ActionsMenu items={actions} slideItems={slideActions} />}
                        </Flex>
                    )}
                </Flex>
                {presentations?.length > 0 && <PresentationsList presentations={presentations} />}
            </Flex>
            {abstractUrl && (
                <AbstractModal
                    open={abstractOpen}
                    onClose={() => setAbstractOpen(false)}
                    url={abstractUrl}
                    title={session.title}
                />
            )}
        </Flex>
    )
}
